import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  Bell,
  Loader2,
  ArrowRight,
  Mail,
  Phone,
  FileText,
  Palette,
  Sparkles,
  Layers,
  Send,
  UserCheck,
  ExternalLink,
} from "lucide-react";
import { plannedActivitiesApi } from "../../lib/plannedActivitiesApi";
import useTenantWebSocket from "../../hooks/useTenantWebSocket";
import { useAuthStore } from "../../store/authStore";
import AssignedByBadge from "../users/AssignedByBadge";

const TYPE_META = {
  email:  { label: "E-posta",  Icon: Mail,     tone: "bg-blue-50 text-blue-700" },
  phone:  { label: "Telefon",  Icon: Phone,    tone: "bg-sky-50 text-sky-700" },
  quote:  { label: "Teklif",   Icon: FileText, tone: "bg-amber-50 text-amber-700" },
  design: { label: "Tasarım",  Icon: Palette,  tone: "bg-fuchsia-50 text-fuchsia-700" },
  custom: { label: "Özel",     Icon: Sparkles, tone: "bg-zinc-50 text-zinc-700" },
};

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function bucketOf(iso) {
  if (!iso) return { key: "future", label: "İleride", tone: "bg-zinc-100 text-zinc-700" };
  const now = new Date();
  const d = new Date(iso);
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfNextDay = new Date(startOfDay.getTime() + 86400000);
  if (d < now) return { key: "overdue", label: "Gecikmiş", tone: "bg-rose-100 text-rose-700" };
  if (d < startOfNextDay) return { key: "today", label: "Bugün", tone: "bg-red-100 text-red-700" };
  const startOfWeekPlus7 = new Date(startOfDay.getTime() + 7 * 86400000);
  if (d < startOfWeekPlus7) return { key: "week", label: "Bu Hafta", tone: "bg-amber-100 text-amber-700" };
  return { key: "future", label: "İleride", tone: "bg-zinc-100 text-zinc-700" };
}

const CARD_VARIANTS = {
  upcoming: {
    title: "Yaklaşan Aktivitelerim",
    Icon: CalendarIcon,
    accent: "text-emerald-600",
    empty: "Sana atanmış bekleyen aktivite yok.",
    showBucket: true,
  },
  assigned_to_me: {
    title: "Bana Atananlar",
    Icon: UserCheck,
    accent: "text-amber-600",
    empty: "Başkası tarafından sana atanmış aktivite yok.",
    showBucket: true,
  },
  all: {
    title: "Tüm Aktivitelerim",
    Icon: Layers,
    accent: "text-violet-600",
    empty: "Henüz atandığın bir aktivite yok.",
    showBucket: true,
  },
  delegated: {
    title: "Delege Ettiklerim",
    Icon: Send,
    accent: "text-sky-600",
    empty: "Bugün / son 25 içinde delege ettiğin aktivite yok.",
    showBucket: false,
  },
};

/**
 * Dashboard widget surfacing planned activities from a user-scoped view.
 * Same component powers 3 variants controlled by `scope`:
 *   - `upcoming` (default): my future to-dos, ascending
 *   - `all`: everything on my plate, most recent first
 *   - `delegated`: activities I created and handed to someone else
 *
 * Auto-refreshes via `planned_activity_changed` WebSocket events. The
 * entire card is a link to `/app/my-activities` (with the same scope
 * pre-selected) so a click drills into the full-page list.
 */
export default function MyPlannedActivitiesCard({ scope = "upcoming", limit = 6 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const variant = CARD_VARIANTS[scope] || CARD_VARIANTS.upcoming;
  const currentUserId = useAuthStore((s) => s.user?.id);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const list = await plannedActivitiesApi.mine({ scope, limit });
      setItems(list);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [scope, limit]);

  useEffect(() => {
    load();
  }, [load]);

  useTenantWebSocket((msg) => {
    if (msg?.event === "planned_activity_changed") load();
  });

  const overdueCount = items.filter(
    (a) => a.scheduled_at && new Date(a.scheduled_at) < new Date()
  ).length;

  return (
    <div
      data-testid={`my-activities-card-${scope}`}
      className="rounded-xl border border-zinc-200 bg-white p-4 transition-shadow hover:shadow-md"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-zinc-900">
          <variant.Icon className={`h-4 w-4 ${variant.accent}`} />
          {variant.title}
          {items.length > 0 && (
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
              scope === "delegated"
                ? "bg-sky-50 text-sky-800"
                : scope === "all"
                  ? "bg-violet-50 text-violet-800"
                  : scope === "assigned_to_me"
                    ? "bg-amber-50 text-amber-800"
                    : "bg-emerald-50 text-emerald-800"
            }`}>
              {items.length}
            </span>
          )}
          {scope === "upcoming" && overdueCount > 0 && (
            <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">
              {overdueCount} gecikmiş
            </span>
          )}
        </div>
        <Link
          to={`/app/my-activities?scope=${scope}`}
          data-testid={`my-activities-open-${scope}`}
          className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
        >
          Tümü <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-zinc-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Yükleniyor…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50/50 p-4 text-center text-xs text-zinc-500">
          {variant.empty}
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {items.map((a) => {
            const meta = TYPE_META[a.activity_type] || TYPE_META.custom;
            const bucket = bucketOf(a.scheduled_at);
            const attributedTo =
              a.created_by && a.created_by !== currentUserId
                ? a.created_by_name
                : null;
            return (
              <li
                key={a.id}
                data-testid={`my-activity-${a.id}`}
                className="py-2"
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    // Ignore clicks that came from an interactive descendant
                    // (the AssignedByBadge popover trigger, etc.). Those
                    // handle their own click and shouldn't drive
                    // navigation.
                    if (e.target.closest("[data-row-noop]")) return;
                    navigate(`/app/cop/construction-tracking/${a.construction_id}`);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/app/cop/construction-tracking/${a.construction_id}`);
                    }
                  }}
                  className="group flex cursor-pointer items-start gap-2.5 rounded-md px-1 py-1 hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  <span
                    className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${meta.tone}`}
                  >
                    <meta.Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-zinc-900">
                        {a.title || "Aktivite"}
                      </span>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${bucket.tone}`}
                      >
                        {bucket.label}
                      </span>
                      {a.reminder_enabled && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                          <Bell className="h-2.5 w-2.5" />
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-500">
                      <span>{fmt(a.scheduled_at)}</span>
                      {a.company_name && <span>· {a.company_name}</span>}
                      {a.stand_no && <span>· Stant #{a.stand_no}</span>}
                      {attributedTo && (
                        <span data-row-noop>
                          <AssignedByBadge
                            userId={a.created_by}
                            userName={attributedTo}
                            userEmail={a.created_by_email}
                            size="sm"
                          />
                        </span>
                      )}
                    </div>
                  </div>
                  <ArrowRight className="mt-1 h-3.5 w-3.5 shrink-0 text-zinc-400 opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
